import React, { useState, useEffect } from "react";
import { FaPlay, FaYoutube, FaSoundcloud, FaSpotify, FaExternalLinkAlt, FaApple, FaBandcamp, FaVimeo } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { getPlatformHandler, getPlatformName } from "../../util/socialMediaUtils";
import SocialButton from "../SocialButton/SocialButton";

const MediaPlayer = ({ social, btnClassName, className, ...rest }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mediaId, setMediaId] = useState(null);
  const [isLoadingId, setIsLoadingId] = useState(true);
  
  const { platform, link } = social;
  
  // Get platform handler
  const platformHandler = getPlatformHandler(link);
  if (!platformHandler) {
    // Fallback to regular social button if platform not supported
    return null;
  }

  const platformName = platformHandler.platform;
  const styling = platformHandler.getStyling();

  // For profile/root URLs, show a regular button instead of an empty player.
  const isSoundCloudProfile = platformName === 'soundcloud' && /soundcloud\.com\/[^\/]+\/?$/.test(link);
  const isVimeoProfile = platformName === 'vimeo' && platformHandler.isProfileUrl(link);
  if (isSoundCloudProfile || isVimeoProfile) {
    return <SocialButton social={social} btnClassName={btnClassName} className={className} eventType={rest.eventType} />;
  }

  // Extract media ID asynchronously
  useEffect(() => {
    const extractMediaId = async () => {
      setIsLoadingId(true);
      try {
        const id = await platformHandler.extractId(link);
        setMediaId(id);
      } catch (error) {
        console.error(`Failed to extract ${platformName} media ID:`, error);
        // For SoundCloud API errors, we might want to show a different message
        if (platformName === 'soundcloud' && error.message?.includes('API')) {
          console.warn('SoundCloud API integration required for this URL type');
        }
        setMediaId(null);
      } finally {
        setIsLoadingId(false);
      }
    };

    extractMediaId();
  }, [link, platformHandler, platformName]);

  // Don't render until we have the media ID
  if (isLoadingId) {
    return (
      <div className={`w-full ${className}`}>
        <div className="w-full h-[80px] bg-gray-200 rounded-kit-md flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  // If no media ID (e.g., YouTube channel/profile, non-embeddable content), 
  // fallback to regular social button
  if (!mediaId) {
    return <SocialButton social={social} btnClassName={btnClassName} className={className} eventType={rest.eventType} />;
  }
  
  const embedUrl = platformHandler.getEmbedUrl(mediaId);
  const thumbnailUrl = platformHandler.getThumbnailUrl(mediaId);

  // Determine if this platform should show embed directly (without thumbnail)
  const shouldShowDirectEmbed = () => {
    return ['soundcloud', 'spotify', 'apple', 'bandcamp'].includes(platformName);
  };
  
  const handlePlayClick = () => {
    setIsPlaying(true);
    setIsLoaded(true);
  };
  
  const handleClosePlayer = () => {
    setIsPlaying(false);
    // Keep isLoaded true to avoid reloading iframe unnecessarily
  };
  
  const openInPlatform = () => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };
  
  const handleIframeError = (e) => {
    console.warn(`${platformName} embed failed`, e);
    console.log(e);
    
  };

  // Get platform-specific icon
  const getPlatformIcon = () => {
    switch (platformName) {
      case 'youtube':
        return <FaYoutube className="text-red-600 drop-shadow-md" size={24} />;
      case 'soundcloud':
        return <FaSoundcloud className="text-orange-500 drop-shadow-md" size={24} />;
      case 'spotify':
        return <FaSpotify className="text-green-500 drop-shadow-md" size={24} />;
      case 'apple':
        return <FaApple className="text-pink-500 drop-shadow-md" size={24} />;
      case 'bandcamp':
        return <FaBandcamp className="text-cyan-600 drop-shadow-md" size={24} />;
      case 'vimeo':
        return <FaVimeo className="text-sky-500 drop-shadow-md" size={24} />;
      default:
        return <FaPlay className="text-white drop-shadow-md" size={24} />;
    }
  };
  
  // Get platform-specific styling and dimensions
  const getPlayerDimensions = () => {
    switch (platformName) {
      case 'soundcloud':
        return 'h-[166px]';
      case 'spotify':
        return 'h-[80px]'; // Triggers Spotify's compact 80px internal height
      case 'apple':
        return 'h-[175px]';
      case 'bandcamp':
        return 'h-[120px]';
      default:
        return 'aspect-video'; // Full video aspect ratio for video platforms
    }
  };

  const getContainerStyling = () => {
    switch (platformName) {
      case 'soundcloud':
        return '';
      case 'spotify':
        return 'bg-gradient-to-r from-gray-900 to-black'; // Dark gradient for Spotify
      case 'apple':
        return 'bg-gradient-to-r from-gray-900 to-black';
      case 'bandcamp':
        return 'bg-gradient-to-r from-gray-800 to-gray-900';
      default:
        return 'bg-black';
    }
  };

  // Show direct embed for audio platforms or when user clicked play
  if (shouldShowDirectEmbed() || isPlaying) {
    return (
      <div className={`w-full ${className}`}>
        {/* Media Player Container */}
        <div className={`relative w-full max-w-full min-w-0 ${
          platformName === "soundcloud"
            ? "rounded-kit-md overflow-hidden"
            : `${getContainerStyling()} rounded-kit-md overflow-hidden shadow-lg`
        }`}>
          {/* Media iframe */}
          <div className={`w-full max-w-full min-w-0 ${getPlayerDimensions()}`}>
            <iframe
              src={embedUrl}
              title={platform || `${platformName} media`}
              className="w-full h-full max-w-full border-0"
              scrolling="no"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen={platformName === 'youtube' || platformName === 'vimeo'}
              referrerPolicy="strict-origin-when-cross-origin"
              onError={handleIframeError}
            />
          </div>
          
          {/* Control buttons - positioned absolutely over the iframe */}
          {/* Only show close button for video platforms when playing */}
          {['youtube', 'vimeo'].includes(platformName) && isPlaying && (
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleClosePlayer();
              }}
              className="absolute top-[0.5rem] right-[0.5rem] bg-black/80 hover:bg-black text-white rounded-kit-full w-[2rem] h-[2rem] flex items-center justify-center transition-all duration-200 z-50"
          >
            <IoMdClose size={14} />
          </button>
          )}
          
          {platformName !== "soundcloud" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openInPlatform();
            }}
            className="absolute top-[0.5rem] left-[0.5rem] bg-black/80 hover:bg-black text-white rounded-kit-full w-[2rem] h-[2rem] flex items-center justify-center transition-all duration-200 z-50"
            title={`Open in ${platformName}`}
          >
            <FaExternalLinkAlt size={10} />
          </button>
          )}
        </div>
        
        {/* Media title/platform name if available */}
        {platform && platform.toLowerCase() !== platformName && (
          <div className="mt-kit-8 text-kit-sm text-[var(--inyc-details-text-color)] font-['Primary-Medium'] text-center">
            {platform}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className={`relative w-full ${className}`}>
      {/* Video Thumbnail with Play Button */}
      <div 
        className="relative cursor-pointer group rounded-kit-md overflow-hidden shadow-md transition-all duration-200 hover:shadow-lg"
        onClick={handlePlayClick}
      >
        {/* Thumbnail */}
        <div className={`w-full bg-gray-200 relative ${shouldShowDirectEmbed() ? 'aspect-[16/7]' : 'aspect-video'}`}>
          {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
              alt={platform || `${platformName} media`}
            className="w-full h-full object-cover"
            onError={(e) => {
                // Fallback to platform gradient if thumbnail fails to load
              e.target.style.display = 'none';
                e.target.parentElement.style.background = styling.gradient || `linear-gradient(135deg, ${styling.primary}, ${styling.accent})`;
              }}
            />
          ) : (
            // No thumbnail available - use platform gradient background
            <div 
              className="w-full h-full"
              style={{ background: styling.gradient || `linear-gradient(135deg, ${styling.primary}, ${styling.accent})` }}
            />
          )}
          
          {/* Dark overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200" />
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black/70 group-hover:bg-black/90 rounded-kit-full p-kit-16 transition-all duration-200 group-hover:scale-110">
              <FaPlay className="text-white ml-kit-2" size={20} />
            </div>
          </div>
          
          {/* Platform logo */}
          <div className="absolute top-[0.5rem] left-[0.5rem]">
            {getPlatformIcon()}
          </div>
        </div>
      </div>
      
      {/* Media title/platform name */}
      {platform && platform.toLowerCase() !== platformName && (
        <div className="mt-kit-8 text-kit-sm text-[var(--inyc-details-text-color)] font-['Primary-Medium'] text-center line-clamp-2">
          {platform}
        </div>
      )}
    </div>
  );
};

export default MediaPlayer;
